import * as React from "react";
import { useState } from "react";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";
import { Label } from "@progress/kendo-react-labels";
import { DatePicker } from "@progress/kendo-react-dateinputs";
import { TextArea } from "@progress/kendo-react-inputs";
import { formatDate } from "@progress/kendo-intl";
import { toast } from "react-toastify";
import { Tile } from "components/UIComponents/Tile";
import DataList from "components/MatterDetails/DataList";
import SubmitButton from "components/common/SubmitButton";
import { AmountFormat, formatDateForAPI } from "components/common/formatter";
import { disputePosting } from "api/accountsAPI";
import styles from "./Posting.module.scss";

const DISPUTABLE_POSTING_TYPE = "PIN";

export const isDisputablePostingType = (postingType) =>
  String(postingType ?? "").toUpperCase() === DISPUTABLE_POSTING_TYPE;

export const getDisputeInfo = (mainData) => {
  const source = mainData?.postingInfo ?? {};
  return {
    isDisputed: Boolean(source.disputed ?? mainData?.disputed),
    dateDisputed: source.dateDisputed ?? mainData?.dateDisputed ?? null,
    disputedBy: source.disputedBy ?? mainData?.disputedBy ?? null,
    details: source.disputeDetails ?? mainData?.disputeDetails ?? "",
  };
};

const DisputePostingDialog = ({
  transNo,
  postingInfo = {},
  postingName,
  disputeInfo,
  onClose,
  onSaved,
}) => {
  const isUndispute = Boolean(disputeInfo?.isDisputed);
  const [date, setDate] = useState(new Date());
  const [details, setDetails] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const netAmount = Number(postingInfo?.netAmount) || 0;
  const vatAmount = Number(postingInfo?.vatAmount) || 0;

  const renderRow = (text, value, minWidth = "100px", valueClass = "mb-2") => (
    <DataList
      text={text}
      value={value}
      keyClass="mb-2"
      valueClass={valueClass}
      width="w-100"
      keyStyles={{ minWidth }}
      valueStyles={{ minWidth: 0, wordBreak: "break-word" }}
    />
  );

  const handleSubmit = async () => {
    setIsSaving(true);
    try {
      const data = {
        dateDisputed: formatDateForAPI(date),
        isDisputed: !isUndispute,
        details,
      };
      const response = await disputePosting(transNo, data);
      if (response?.status === 200) {
        toast.success(
          isUndispute
            ? "Posting undisputed successfully"
            : "Posting disputed successfully",
        );
        onSaved?.();
      }
    } catch (error) {
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      title={isUndispute ? "Undispute Posting" : "Dispute Posting"}
      onClose={onClose}
      className="dialog-xxs"
    >
      {!isUndispute && (
        <Tile className="py-3 px-4 mt-3 mx-4">
          <div className="w-100 mb-2">
            <span className={styles["dialog-section-heading"]}>
              {`${postingInfo?.postingType}${postingName ? ` - ${postingName}` : ""}`}
            </span>
          </div>
          <div className="d-flex">
            <div style={{ width: "60%" }}>
              {renderRow(
                "Date:",
                postingInfo?.date
                  ? formatDate(new Date(postingInfo.date), "d", "en-GB")
                  : "",
              )}
              {renderRow("Reference:", postingInfo?.reference)}
              {renderRow("Details:", postingInfo?.details)}
            </div>
            <div style={{ width: "40%" }}>
              {renderRow("Net:", AmountFormat(netAmount), "60px", "text-end mb-2")}
              {renderRow("VAT:", AmountFormat(vatAmount), "60px", "text-end mb-2")}
              {renderRow(
                "Gross:",
                AmountFormat(netAmount + vatAmount),
                "60px",
                "text-end mb-2",
              )}
            </div>
          </div>
        </Tile>
      )}

      {isUndispute && (
        <Tile className="py-3 px-4 mt-3 mx-4" heading="Dispute Details" headingClass="pt-1">
          {renderRow(
            "Date Disputed:",
            disputeInfo?.dateDisputed
              ? formatDate(new Date(disputeInfo.dateDisputed), "d", "en-GB")
              : "",
            "115px",
          )}
          {renderRow("Disputed By:", disputeInfo?.disputedBy || "", "115px")}
          {disputeInfo?.details
            ? renderRow("Reason:", disputeInfo.details, "115px")
            : null}
        </Tile>
      )}

      <Tile className="py-3 px-4 mt-3 mb-2 mx-4">
        <div className="d-flex flex-column">
          <Label className="mb-1">
            {isUndispute ? "Undispute Date" : "Dispute Date"}
          </Label>
          <DatePicker
            className="date-picker px-1"
            width="265px"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>
        <div className="mt-3 d-flex flex-column">
          <Label className="mb-1">
            {isUndispute ? "Undispute Reason" : "Dispute Reason"}
          </Label>
          <TextArea
            className="w-100"
            placeholder={
              isUndispute
                ? "Write reason for removing the dispute"
                : "Write reason for dispute"
            }
            style={{ height: "75px", maxWidth: "100%" }}
            value={details}
            onChange={(e) => setDetails(e.target.value)}
          />
        </div>
      </Tile>

      <DialogActionsBar>
        <div className="dialog-footer w-100">
          <Button
            className="common-btn bgBtnSecondary border-0 me-3"
            onClick={onClose}
          >
            Close
          </Button>
          <SubmitButton
            type="button"
            className="border-0 me-5"
            onClick={handleSubmit}
            disabled={!date || isSaving}
            isLoading={isSaving}
          >
            {isUndispute ? "Undispute" : "Dispute"}
          </SubmitButton>
        </div>
      </DialogActionsBar>
    </Dialog>
  );
};

export default DisputePostingDialog;
